import { useTranslation } from "react-i18next";
import { Shield, Lock, TrendingUp } from "lucide-react";
import CryptoLogo from "./CryptoLogo";

const CRYPTO_COLORS = {
  BTC: "#F7931A", ETH: "#627EEA", ADA: "#0033AD", DOGE: "#C2A633",
  SOL: "#9945FF", AVAX: "#E84142", MATIC: "#8247E5", NEAR: "#00C08B", TON: "#0098EA",
};

export default function CryptoCard({ card, selected, onClick, assetBonus }) {
  const { t } = useTranslation();
  // Hidden / face-down variant — used when rendering an opponent's cards
  // in AI / Daily / future online PvP. Preserves layout and spacing so the
  // board still looks full.
  if (card?.hidden) {
    return (
      <div
        data-testid={`crypto-card-hidden-${card.id}`}
        className="crypto-card relative flex-shrink-0 w-[4.75rem] sm:w-24 rounded-2xl border-2 border-[#1A237E]/30 dark:border-[#E3F2FD]/30 overflow-hidden select-none bg-gradient-to-br from-[#1A237E] to-[#38B6FF] shadow-[inset_0_0_24px_rgba(255,255,255,0.08)]"
      >
        <div className="h-1.5 bg-[#8247E5]" />
        <div className="p-2 flex flex-col items-center justify-center h-[80%] text-white/90">
          <Lock className="w-5 h-5 opacity-60" />
          <p className="text-[9px] font-bold uppercase tracking-[0.2em] opacity-70 mt-1" style={{ fontFamily: "Fredoka" }}>
            {t("cryptoCard.label.hidden")}
          </p>
        </div>
      </div>
    );
  }

  const color = CRYPTO_COLORS[card.symbol] || "#888";
  const isFrozen = card.frozen;
  const isHedged = card.hedged;
  const hasBonus = assetBonus && assetBonus > 0;

  return (
    <div
      data-testid={`crypto-card-${card.id}`}
      className={`crypto-card relative flex-shrink-0 w-[4.75rem] sm:w-24 rounded-2xl border-2 overflow-hidden cursor-pointer select-none ${
        selected ? 'ring-2 ring-[#38B6FF] border-[#38B6FF] -translate-y-2' : 'border-black/10'
      }`}
      style={{ background: `linear-gradient(135deg, ${color}15, ${color}30)` }}
      onClick={onClick}
    >
      {/* Top accent bar */}
      <div className="h-1.5" style={{ background: color }} />

      <div className="p-2 text-center">
        {/* Logo */}
        <div className="flex justify-center mb-1">
          <CryptoLogo symbol={card.symbol} size={28} />
        </div>

        {/* Symbol */}
        <p className="text-xs font-extrabold tracking-wide" style={{ color, fontFamily: 'Fredoka' }}>
          {card.symbol}
        </p>

        {/* Printed value */}
        <p className="text-lg font-bold mt-0.5 text-[#1A237E] dark:text-[#E3F2FD]" style={{ fontFamily: 'Fredoka' }}>
          ${card.printed_value}
        </p>

        {/* Asset class bonus indicator */}
        {hasBonus && (
          <p className="text-[10px] font-bold text-[#00E676] flex items-center justify-center gap-0.5">
            <TrendingUp className="w-2.5 h-2.5" /> +${assetBonus}
          </p>
        )}

        {/* Status icons */}
        <div className="flex items-center justify-center gap-1 mt-1">
          {isFrozen && <Shield className="w-3 h-3 text-blue-500" />}
          {isHedged && <Lock className="w-3 h-3 text-green-500" />}
        </div>
      </div>
    </div>
  );
}
